import CodeInput from 'components/CodeInput';
import Breadcrumbs from 'components/Common/Breadcrumb';
import Loader from 'components/Loader';
import SmkAsyncSelect from 'components/SmkAsyncSelect';
import { useFormik } from 'formik';
import useRequest from 'hooks/useRequest';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';
import { API_BASE_URL } from 'utils/constants';
import { titleCaseIfExists } from 'utils/helper';
import { SMKLinks } from 'utils/links';
import * as Yup from 'yup';

const AddUpdateSubscriptionGiftCards = () => {
  const { id: giftCardId } = useParams();
  const history = useHistory();
  toast.configure();

  const [giftCardDetails, setGiftCardDetails] = useState({});

  const sentOptions = [
    { value: 'email', label: 'Email' },
    { value: 'phoneNumber', label: 'Phone Number' },
  ];

  const [getGiftCardRequest, { loading: getGiftCardLoading }] = useRequest({
    url: `/subscription/offers/admin/subscription-gift-card/${giftCardId}`,
    method: 'GET',
    onSuccess: (data) => {
      setGiftCardDetails(data);
    },
    onError: (err) => {
      toast.error(err?.message);
      console.log(err);
    },
  });

  const [addGiftCardRequest, { loading: addGiftCardLoadng }] = useRequest({
    url: '/subscription/offers/admin/subscription-gift-card',
    method: 'POST',
    onSuccess: (data) => {
      const url = SMKLinks.SUBSCRIPTION_GIFT_CARDS;
      history.replace(url);
      toast.success('Success! Subscription Gift Card has been added.');
    },
    onError: (err) => {
      toast.error(err?.message);
      console.log(err);
    },
  });

  const [updateGiftCardRequest, { loading: updateGiftCardLoading }] = useRequest({
    url: `/subscription/offers/admin/subscription-gift-card/${giftCardId}`,
    method: 'PUT',
    onSuccess: (data) => {
      toast.success('Success! Subscription Gift Card has been updated.');
      window.location.reload();
    },
    onError: (err) => {
      toast.error(err?.message);
      console.log(err);
    },
  });

  const editInitialValues = () => ({
    active: giftCardDetails?.active,
    deleted: giftCardDetails?.deleted,
    subscription: giftCardDetails?.subscriptionPlan,
    price: giftCardDetails?.price,
    code: giftCardDetails?.code,
    externalSource: giftCardDetails?.externalSource,
    externalId: giftCardDetails?.externalId,
    personalMessage: giftCardDetails?.personalMessage || '',

    sentToName: giftCardDetails?.sentToName || '',
    sentToEmail: giftCardDetails?.sentToEmail || '',
    sentToPhoneNumber: giftCardDetails?.sentToPhoneNumber || '',

    fromName: giftCardDetails?.fromName || '',
    fromEmail: giftCardDetails?.fromEmail || '',
    fromPhoneNumber: giftCardDetails?.fromPhoneNumber || '',
  });

  const getInitialValues = () => ({
    active: true,
    deleted: false,
    subscription: null,
    price: 0,
    code: '',
    externalSource: '',
    externalId: '',
    personalMessage: '',

    sentToName: '',
    sentToEmail: '',
    sentToPhoneNumber: '',

    fromName: '',
    fromEmail: '',
    fromPhoneNumber: '',
  });

  const checkNotEmpty = (val) => {
    return val === '' || val === null || val === undefined;
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: isEmpty(giftCardId) ? { ...getInitialValues() } : { ...editInitialValues() },
    onSubmit: async (values) => {
      if (isEmpty(values?.sentToEmail) && checkNotEmpty(values?.sentToPhoneNumber)) {
        toast.error('Please enter sender email or phone number');
        return;
      }

      if (isEmpty(values?.fromEmail) && checkNotEmpty(values?.fromPhoneNumber)) {
        toast.error('Please enter reciever email or phone number');
        return;
      }

      const payload = {
        body: {
          active: values?.active,
          deleted: values?.deleted,
          subscriptionPlanId: values?.subscription?.id || values?.subscription?.value,
          code: values?.code,
          price: values?.price,
          expiryDate: moment()
            ?.add(6, 'months')
            ?.endOf('day')
            ?.toISOString(),
          externalSource: values?.externalSource,
          externalId: values?.externalId,
          personalMessage: values?.personalMessage,

          sentToName: values?.sentToName || '',
          sentToEmail: values?.sentToEmail || '',
          sentToPhoneNumber: values?.sentToPhoneNumber || '',

          fromName: values?.fromName || '',
          fromEmail: values?.fromEmail || '',
          fromPhoneNumber: values?.fromPhoneNumber || '',
        },
      };

      if (isEmpty(giftCardId)) {
        addGiftCardRequest(payload);
      } else {
        updateGiftCardRequest(payload);
      }
    },
    validationSchema: Yup.object({
      subscription: Yup.object()
        .nullable(true)
        .required('Please enter subscription plan'),
      price: Yup.number()
        .min(1)
        .required('Please enter price'),
      code: Yup.string().required('Please enter code'),
      sentToName: Yup.string().required('Please enter name'),
      fromName: Yup.string().required('Please enter name'),

      sentToPhoneNumber: Yup.string().matches(/^\d{10,12}$/, 'Phone number must be between 10 and 12 digits'),

      fromPhoneNumber: Yup.string().matches(/^\d{10,12}$/, 'Phone number must be between 10 and 12 digits'),
    }),
  });

  useEffect(() => {
    if (!isEmpty(giftCardId)) {
      getGiftCardRequest();
    }
  }, [giftCardId]);

  const [generateGiftCardRequest, { loading: generateGiftCardLoading }] = useRequest({
    url: '/subscription/offers/admin/subscription-gift-card/generate-gift-code',
    method: 'GET',
    onSuccess: (code) => {
      formik.setFieldValue('code', code, false);
    },
    onError: (err) => {
      toast.error(err?.message);
      console.log(err);
    },
  });

  const handleGenerateCode = async () => {
    await generateGiftCardRequest();
  };

  useEffect(() => {
    generateGiftCardRequest();
  }, []);

  return (
    <div className="page-content">
      <Loader isActive={addGiftCardLoadng || getGiftCardLoading || updateGiftCardLoading || generateGiftCardLoading} />
      <Container fluid>
        <Breadcrumbs
          title="Subscription Gift Card"
          breadcrumbItem={`${isEmpty(giftCardId) ? 'Add' : 'Edit'}  Subscription Gift Card`}
        />

        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col md={9}>
              <Card>
                <CardBody>
                  <Row className="mx-0">
                    <div className="mb-4">
                      <Label>
                        Subscription Plan
                        <span className="text-danger">*</span>
                      </Label>
                      <SmkAsyncSelect
                        isClearable={false}
                        combineLabelKey={'currency'}
                        acceptedKey={'name'}
                        acceptedValue={'id'}
                        placeholder={'Search for subscription name...'}
                        onChange={(res) => {
                          formik.setFieldValue('subscription', res);
                        }}
                        apiMethod={'POST'}
                        fetchUrl={`${API_BASE_URL}/subscription/admin/subscription-plan/search`}
                        filters={{
                          page: 1,
                          size: 20,
                          sortBy: 'name',
                          sortOrder: 0,
                          active: true,
                        }}
                        searchKeyName="name"
                        isQueryParam={true}
                        value={
                          !isEmpty(formik.values?.subscription)
                            ? {
                                label: `${formik.values.subscription?.currency} - ${formik.values.subscription?.name}`,
                                value: formik.values.subscription?.id,
                              }
                            : null
                        }
                      />
                      {formik?.errors?.subscription && (
                        <FormFeedback className="d-block">{formik.errors?.subscription}</FormFeedback>
                      )}
                    </div>

                    <CustomInput
                      required={true}
                      name="price"
                      type="number"
                      placeholder="Price"
                      col={12}
                      formik={formik}
                    />

                    <div className="mb-4">
                      {/* <CodeInput
                        required={true}
                        disabled={true}
                        isError={formik.errors.code}
                        value={formik.values.code}
                        onChange={(val) => {
                          formik.setFieldValue('code', val);
                        }}
                      /> */}

                      <CustomInput
                        disabled={true}
                        required={true}
                        name="code"
                        type="text"
                        placeholder="Code"
                        col={12}
                        formik={formik}
                      />

                      <button type="button" className="btn btn-sm btn-success" onClick={handleGenerateCode}>
                        Generate Code
                      </button>
                    </div>

                    <CustomInput
                      name="externalSource"
                      type="text"
                      placeholder="External Source"
                      col={12}
                      formik={formik}
                      suggestion={'Example: Amazon, Flipkart'}
                    />

                    <CustomInput name="externalId" type="text" placeholder="External Id" col={12} formik={formik} />
                    <CustomInput
                      name="personalMessage"
                      type="textarea"
                      rows={5}
                      placeholder="Personal Message"
                      col={12}
                      formik={formik}
                    />
                  </Row>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="mb-4">
                <CardBody>
                  <Button color={'primary'} className="w-100" size="md" type="submit">
                    {!isEmpty(giftCardId) ? 'Update' : 'Save'}
                  </Button>
                  <Link to={'/subscription-gift-cards'} className="btn btn-light w-100 mt-3">
                    Cancel
                  </Link>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  {['active', 'deleted']?.map((elem, index) => (
                    <FormGroup className={`${'mb-3'}`} key={index}>
                      <Label className="mb-3">{titleCaseIfExists(elem)} </Label>
                      <div className="d-flex align-items-center">
                        {[true, false].map((option, i) => (
                          <div key={`${elem}_${i}`} className="form-check d-inline-block me-4">
                            <input
                              type="radio"
                              id={`${elem}_${option}`}
                              name={elem}
                              className="form-check-input"
                              checked={formik.values[elem] === option}
                              onChange={() => formik.setFieldValue(`${elem}`, option)}
                            />
                            <label className="form-check-label" htmlFor={`${elem}_${option}`}>
                              {option ? 'Yes' : 'No'}
                            </label>
                          </div>
                        ))}
                      </div>
                    </FormGroup>
                  ))}
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <CardTitle className="border-bottom pb-2 mb-4">Sender Details</CardTitle>
                  <CustomInput
                    required={true}
                    name="fromName"
                    type="text"
                    placeholder="Name"
                    col={12}
                    formik={formik}
                  />

                  <CustomInput name="fromEmail" type="text" placeholder="Email" col={12} formik={formik} />

                  <CustomInput
                    name="fromPhoneNumber"
                    type="number"
                    placeholder="Phone Number"
                    col={12}
                    formik={formik}
                  />
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <CardTitle className="border-bottom pb-2 mb-4">Reciever Details</CardTitle>
                  <CustomInput
                    required={true}
                    name="sentToName"
                    type="text"
                    placeholder="Name"
                    col={12}
                    formik={formik}
                  />

                  <CustomInput name="sentToEmail" type="text" placeholder="Email" col={12} formik={formik} />

                  <CustomInput
                    name="sentToPhoneNumber"
                    type="number"
                    placeholder="Phone Number"
                    col={12}
                    formik={formik}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

const CustomInput = ({
  name,
  type,
  placeholder,
  col,
  className,
  rows,
  formik,
  suggestion,
  required = false,
  disabled = false,
}) => {
  return (
    <FormGroup className={`mb-4 col-md-${col} ${className ? className : ''}`}>
      <Label htmlFor={name} className="form-label ">
        {placeholder} {required && <span className="text-danger">*</span>}
      </Label>
      <Input
        name={name}
        type={type}
        className="form-control"
        placeholder={`Enter ${placeholder}`}
        onChange={formik?.handleChange}
        value={formik?.values[name]}
        rows={rows}
        invalid={formik?.errors[name] ? true : false}
        disabled={disabled}
      />
      {formik?.errors[name] && <FormFeedback className="d-block">{formik.errors[name]}</FormFeedback>}
      {!isEmpty(suggestion) && <small>{suggestion}</small>}
    </FormGroup>
  );
};

export default AddUpdateSubscriptionGiftCards;
